import cn from 'classnames';
import { serialize } from 'object-to-formdata';
import React, { useEffect } from 'react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icon, Image } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import Modal from 'components/Modal';
import Tooltip from 'components/Tooltip';
import Interests from 'components/candidate/Onboarding/Interests';
import { Button, Form } from 'components/form';
import { Col, Row } from 'components/layout';
import { useSocietyProfile } from 'pages/society-profile/UseSocietyProfile.hook';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { TUserApiResponse } from 'lib/models/User.model';
import { StudentSocietyResponse } from 'lib/models/student-society';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { http } from 'lib/utils/http';
import {
  parseArrayResponse,
  parseErrorResponse,
  parseResponse,
} from 'lib/utils/parser';
import { REGEX } from 'lib/utils/regex';

import Footer from '../Footer';
import Header from '../Header';
import StepHeading from '../StepHeading';
import styles from './Step4.module.scss';

interface Step4Props {
  progress: number;
  setStep: (step: number) => void;
}
interface InterestInput {
  interestable_id: string;
}

interface Topics {
  id: string;
  isPrimary: boolean;
}

interface SocietyAttributes {
  logo: string | FileList;
  short_description: string;
  topics: Topics[];
  instagram_url: string;
  facebook_url: string;
  linkedin_url: string;
  website: string;
  student_union_url: string;
}

export const Step4: React.FC<Step4Props> = ({ progress, setStep }) => {
  const { updateUserApiResponse, manager, managerSociety } = useAuth();
  const { topics: topicOptions } = useSocietyProfile();
  const [t] = useTranslation('signup.manager');

  const [initialTopics, setInitialTopics] = React.useState<Topics[]>([]);

  useEffect(() => {
    const primaryTopic = parseResponse(managerSociety?.primary_topic);
    const nonPrimaryTopics = parseArrayResponse(managerSociety?.topics);
    const initialTopics = primaryTopic
      ? [{ id: primaryTopic.id, isPrimary: true }]
      : [];

    nonPrimaryTopics.forEach((element) => {
      initialTopics.push({ id: element.id, isPrimary: false });
    });
    setInitialTopics(initialTopics);
  }, [managerSociety]);

  useEffect(() => {
    const topics = initialTopics?.map(
      (topic) =>
        ({
          interestable_id: topic.id,
        } as InterestInput)
    );
    setSelectedTopic(topics || []);
    setValue('topics', initialTopics);
  }, [initialTopics]);

  const emptyFileList = new DataTransfer().files;
  const icon_url: string | FileList = managerSociety?.icon_url || emptyFileList;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
    setError,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      logo: icon_url,
      short_description: managerSociety?.short_description,
      topics: initialTopics,
      instagram_url: managerSociety?.instagram_url,
      facebook_url: managerSociety?.facebook_url,
      linkedin_url: managerSociety?.linkedin_url,
      website: managerSociety?.website,
      student_union_url: managerSociety?.student_union_url,
    },
  });

  const [showError, setShowError] = React.useState<string>('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [selectedTopic, setSelectedTopic] = React.useState<InterestInput[]>([]);

  const handleNextClick = (selectedInterests: InterestInput[]) => {
    setSelectedTopic(selectedInterests);

    const values =
      selectedInterests?.map((topic: InterestInput) => topic.interestable_id) ||
      [];

    const topicValues = values.map((value: string, index: number) => ({
      id: value,
      isPrimary: index === 0,
    }));
    setValue('topics', topicValues);

    clearErrors('topics');

    setIsModalOpen(false);
  };

  const onSubmit: SubmitHandler<SocietyAttributes> = async (data) => {
    if (isModalOpen) {
      return;
    }
    if (data.topics?.length === 0) {
      setError('topics', { message: t('step-4.topics.error-message') });
      return;
    }

    const { logo, topics, ...rest } = data;
    const primary_topic_id = topics?.filter((topic) => topic.isPrimary)[0]?.id;
    const topic_ids =
      topics &&
      topics
        .filter((top) => !top?.isPrimary)
        .map((top: { id: string }) => top?.id);

    const body = {
      student_society: {
        ...rest,
        ...(typeof logo !== 'string' && {
          icon: logo && logo[0],
        }),
        primary_topic_id,
      },
    };

    const formData = serialize(body, { indices: true });

    if (!manager) return;
    const plainHeaders: HeadersInit = new Headers();
    plainHeaders.set('X-Auth-Email', manager.email);
    plainHeaders.set('X-Auth-Token', manager.authentication_token);

    try {
      await http<StudentSocietyResponse>(API_ROUTES.STUDENT_SOCIETY, {
        method: 'PUT',
        body: formData,
        headers: plainHeaders,
      });

      await apiInstance.patch<StudentSocietyResponse>(
        API_ROUTES.STUDENT_SOCIETY,
        { topic_ids }
      );

      const manager = { flow_state: ONBOARDING_FLOW_STATE.PRE_5 };
      const { data: response } = await apiInstance.patch<TUserApiResponse>(
        API_ROUTES.MANAGER_USER,
        {
          manager,
        }
      );

      updateUserApiResponse(response);
      setStep(5);
    } catch (error) {
      const message = parseErrorResponse(error as Error);
      setShowError(message);
    }
  };
  const { fields: list } = useFieldArray({
    control,
    name: 'topics',
  });

  register('logo', { required: t('step-4.logo.required') });
  register('short_description', {
    required: t('step-4.short-description.required'),
  });

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const files: FileList = target.files as FileList;
    setValue('logo', files, { shouldDirty: true });
    setError('logo', {});
  };

  const logo = watch('logo');

  const logoImage =
    typeof logo === 'object' && logo?.length > 0 && logo !== null
      ? URL.createObjectURL(logo[0])
      : typeof logo === 'object' && logo !== null
      ? typeof logo === 'object' && logo.length === 0
        ? ''
        : URL.createObjectURL(logo as unknown as Blob)
      : typeof logo === 'string'
      ? logo
      : '';

  const makePrimary = (id: string) => {
    const topicValues = list?.map((topic) => ({
      id: topic.id,
      isPrimary: topic.id === id,
    }));
    setValue('topics', topicValues);
  };

  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          setStep(3);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-4.header.title')}
          desc={t('step-4.header.desc')}
        />

        <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Row className={styles.imageUploader}>
            <Col span={24}>
              <div className={styles.fieldLabel}>
                <span> {t('step-4.logo.label')}</span>
              </div>
              <input
                type="file"
                accept="image/*"
                id={`upload-profile-image`}
                hidden
                onChange={uploadImage}
              />
              {logoImage ? (
                <div className={styles.uploadedFile}>
                  <div className={styles.avatarWrapper}>
                    <Image src={logoImage} className={styles.image} />
                  </div>

                  <label
                    htmlFor={`upload-profile-image`}
                    className={styles.changePhoto}
                  >
                    <Icon iconName="edit-v2" />
                    {t('step-4.logo.change')}
                  </label>

                  <Button
                    type="button"
                    color="secondary"
                    className={styles.changePhoto}
                    onClick={() => setValue('logo', '')}
                  >
                    <Icon iconName="trash" />
                    {t('step-4.logo.remove')}
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor={`upload-profile-image`}
                  className={cn(styles.mainButton, {
                    [styles.error]: !!errors.logo?.message,
                  })}
                >
                  {t('step-4.logo.upload')}
                </label>
              )}
              {errors.logo?.message && (
                <div className={styles.errorMessage}>
                  {errors.logo?.message}
                </div>
              )}
            </Col>
          </Row>

          <Row className={styles.mb24}>
            <Col span={24}>
              <InputV2
                label={t('step-4.short-description.label')}
                {...register('short_description', {
                  required: t('step-4.short-description.required'),
                })}
                placeholder={t('step-4.short-description.placeholder')}
                isInvalid={!!errors.short_description}
                labelClassName={styles.fieldLabel}
              />

              {errors.short_description?.message && (
                <div className={styles.errorMessage}>
                  {errors.short_description?.message}
                </div>
              )}
            </Col>
          </Row>

          <Row className={styles.imageUploader}>
            <Col span={24}>
              <div className={styles.fieldLabel}>
                <span>{t('step-4.topics.title')}</span>
                <Tooltip
                  content={t('step-4.topics.tooltip')}
                  position="top"
                  xAlign="center"
                  color="dark"
                  className={styles.tooltipContainer}
                >
                  <Icon
                    iconName="icon_info-outline"
                    className={styles.tooltipContainerIcon}
                    size="small"
                  />
                </Tooltip>
              </div>

              {list &&
                list.length > 0 &&
                list?.map(
                  (topic: { id: string; isPrimary?: boolean }, index) => {
                    const selectedValueLabel =
                      topicOptions.filter(
                        (option) => option.value === topic.id
                      )[0]?.label || '';
                    return (
                      <div className={styles.topicWrapper} key={index}>
                        <div className={styles.selectedTopic}>
                          {selectedValueLabel}
                        </div>

                        {topic.isPrimary ? (
                          <div className={styles.primary}>
                            <Icon iconName="icon_check-double" />{' '}
                            {t('step-4.topics.focus')}
                          </div>
                        ) : (
                          <div
                            className={styles.makePrimary}
                            onClick={() => makePrimary(topic.id)}
                          >
                            {t('step-4.topics.set-focus')}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}

              {isModalOpen && (
                <Modal
                  isOpen={isModalOpen}
                  onRequestClose={() => setIsModalOpen(false)}
                  className={styles['topic-modal']}
                >
                  <Interests
                    className={styles.intrest}
                    openOnModal={true}
                    topics={topicOptions}
                    candidateInterests={selectedTopic}
                    onNextClick={handleNextClick}
                    onLeftClick={function (): void {
                      throw new Error('Function not implemented.');
                    }}
                    step={0}
                    totalStep={0}
                    isLoading={false}
                  />
                </Modal>
              )}
              <button
                className={cn(styles.mainButton, {
                  [styles.error]: !!errors.topics,
                })}
                onClick={() => setIsModalOpen(true)}
              >
                {t('step-4.topics.add-topics')}
              </button>
              {errors.topics && (
                <div className={styles.errorMessage}>
                  {(errors.topics as unknown as { message: string }).message}
                </div>
              )}
            </Col>
          </Row>

          <Row className={styles.imageUploader}>
            <Col span={24}>
              <div className={styles.fieldLabel}>
                <span>
                  <span>{t('step-4.social-media-link.title')}</span>
                  <span className={styles.optional}>({t('optional')})</span>
                </span>
              </div>
            </Col>

            <Col span={24} className={styles.socialLinkWrapper}>
              <div className={styles.socialLinkInput}>
                <Image
                  className={styles.socialIcon}
                  src="/images/instagram.png"
                  alt={t('step-4.social-media-link.instagram.alt')}
                />
                <InputV2
                  {...register('instagram_url', {
                    pattern: {
                      value: REGEX.URL,
                      message: t('invalid-url'),
                    },
                  })}
                  placeholder={t(
                    'step-4.social-media-link.instagram.placeholder'
                  )}
                  isInvalid={!!errors.instagram_url}
                  labelClassName={styles.fieldLabel}
                />
              </div>
              {errors.instagram_url?.message && (
                <div className={styles.errorMessage}>
                  {errors.instagram_url?.message}
                </div>
              )}
            </Col>

            <Col span={24} className={styles.socialLinkWrapper}>
              <div className={styles.socialLinkInput}>
                <Image
                  className={styles.socialIcon}
                  src="/images/facebook.png"
                  alt={t('step-4.social-media-link.facebook.alt')}
                />
                <InputV2
                  {...register('facebook_url', {
                    pattern: {
                      value: REGEX.URL,
                      message: t('invalid-url'),
                    },
                  })}
                  placeholder={t(
                    'step-4.social-media-link.facebook.placeholder'
                  )}
                  isInvalid={!!errors.facebook_url}
                  labelClassName={styles.fieldLabel}
                />
              </div>
              {errors.facebook_url?.message && (
                <div className={styles.errorMessage}>
                  {errors.facebook_url?.message}
                </div>
              )}
            </Col>

            <Col span={24} className={styles.socialLinkWrapper}>
              <div className={styles.socialLinkInput}>
                <Image
                  className={styles.socialIcon}
                  src="/images/linkedin.png"
                  alt={t('step-4.social-media-link.linkedin.alt')}
                />
                <InputV2
                  {...register('linkedin_url', {
                    pattern: {
                      value: REGEX.URL,
                      message: t('invalid-url'),
                    },
                  })}
                  placeholder={t(
                    'step-4.social-media-link.linkedin.placeholder'
                  )}
                  isInvalid={!!errors.linkedin_url}
                  labelClassName={styles.fieldLabel}
                />
              </div>
              {errors.linkedin_url?.message && (
                <div className={styles.errorMessage}>
                  {errors.linkedin_url?.message}
                </div>
              )}
            </Col>

            <Col span={24} className={styles.socialLinkWrapper}>
              <div className={styles.socialLinkInput}>
                <Image
                  className={styles.socialIcon}
                  src="/images/web-link.png"
                  alt={t('step-4.social-media-link.web-link.alt')}
                />
                <InputV2
                  {...register('website', {
                    pattern: {
                      value: REGEX.URL,
                      message: t('invalid-url'),
                    },
                  })}
                  placeholder={t(
                    'step-4.social-media-link.web-link.placeholder'
                  )}
                  isInvalid={!!errors.website}
                  labelClassName={styles.fieldLabel}
                />
              </div>
              {errors.website?.message && (
                <div className={styles.errorMessage}>
                  {errors.website?.message}
                </div>
              )}
            </Col>

            <Col span={24} className={styles.socialLinkWrapper}>
              <div className={styles.socialLinkInput}>
                <Image
                  className={styles.socialIcon}
                  src="/images/student-union.png"
                  alt={t('step-4.social-media-link.student-union.alt')}
                />
                <InputV2
                  {...register('student_union_url', {
                    pattern: {
                      value: REGEX.URL,
                      message: t('invalid-url'),
                    },
                  })}
                  placeholder={t(
                    'step-4.social-media-link.student-union.placeholder'
                  )}
                  isInvalid={!!errors.student_union_url}
                  labelClassName={styles.fieldLabel}
                />
              </div>
              {errors.student_union_url?.message && (
                <div className={styles.errorMessage}>
                  {errors.student_union_url?.message}
                </div>
              )}
            </Col>
          </Row>

          {showError && <div className={styles.errorMessage}>{showError}</div>}
          <Footer>
            <ButtonV3
              type="submit"
              isFullWidth
              isLoading={isSubmitting}
              size="xlarge"
            >
              {t('next')}
            </ButtonV3>
          </Footer>
        </Form>
      </div>
    </>
  );
};
